import React from 'react';
import { useNavigate } from 'react-router-dom';
import { User, LogOut } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import type { RootState } from '../../store';
import { setShowAdminDetails } from '../../store/slices/uiSlice';
import { Dropdown } from '../ui/dropdown/Dropdown';
import { DropdownItem } from '../ui/dropdown/DropdownItem';
import { PRIMARY_GREEN } from '../sidenavbar/SideNavbar';

interface AdminDetailsModalProps {
    adminName?: string;
    adminMobile?: string;
    adminRole?: string;
    adminReferralCode?: string;
    onLogout?: () => void;
}

// TailAdmin's UserDropdown, ported: an anchored popover under the profile
// trigger (not a centered modal) — name/mobile header, then a nav-item list,
// then a sign-out row, all inside one rounded-2xl card.
const AdminDetailsModal: React.FC<AdminDetailsModalProps> = (props) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const isOpen = useAppSelector((state: RootState) => state.ui.showAdminDetails);
    const auth = useAppSelector((state: RootState) => state.auth);

    const adminName = props.adminName || auth.adminName;
    const adminMobile = props.adminMobile || auth.adminMobile;
    const adminRole = props.adminRole || auth.adminRole;

    const adminProfile = useAppSelector((state: RootState) => state.users.adminProfile);
    const adminReferralCode = props.adminReferralCode || adminProfile?.referral_code || '';

    const onClose = () => {
        dispatch(setShowAdminDetails(false));
    };

    const onViewProfile = () => {
        onClose();
        if (adminMobile) {
            navigate(`/profile/${adminMobile}`);
        }
    };

    return (
        <Dropdown
            isOpen={isOpen}
            onClose={onClose}
            className="right-[24px] md:right-0 mt-[17px] flex w-[calc(100vw-3rem)] max-w-[260px] flex-col rounded-2xl border border-gray-200 bg-white p-3 shadow-theme-lg"
        >
            <div className="flex items-center gap-3 px-1 pb-1">
                <div
                    className="w-10 h-10 rounded-full text-sm font-bold flex items-center justify-center shrink-0"
                    style={{ backgroundColor: `${PRIMARY_GREEN}1A`, color: PRIMARY_GREEN }}
                >
                    {adminName?.charAt(0)}
                </div>
                <div className="overflow-hidden">
                    <span className="block font-medium text-gray-700 text-theme-sm truncate">
                        {adminName}
                    </span>
                    <span className="mt-0.5 block text-theme-xs text-gray-500 truncate">
                        {adminMobile}
                    </span>
                </div>
            </div>

            {adminRole && (
                <div className="mt-3 rounded-lg bg-gray-50 px-3 py-2">
                    <span className="block text-theme-xs text-gray-500">Role</span>
                    <span className="block text-theme-sm font-semibold text-gray-800">
                        {adminRole}
                    </span>
                </div>
            )}

            {adminReferralCode && (
                <div className={`${adminRole ? 'mt-2' : 'mt-3'} rounded-lg bg-gray-50 px-3 py-2`}>
                    <span className="block text-theme-xs text-gray-500">Referral Code</span>
                    <span className="block text-theme-sm font-semibold text-gray-700 font-mono tracking-wider">
                        {adminReferralCode}
                    </span>
                </div>
            )}

            <ul className="flex flex-col gap-1 pt-3 pb-3 border-b border-gray-200">
                <li>
                    <DropdownItem
                        onItemClick={onViewProfile}
                        className="flex items-center gap-3 px-3 py-2 font-medium text-gray-700 rounded-lg group text-theme-sm hover:bg-gray-100 hover:text-gray-700"
                    >
                        <User size={18} className="text-gray-500 group-hover:text-gray-700" />
                        View Profile
                    </DropdownItem>
                </li>
            </ul>

            <button
                onClick={() => {
                    onClose();
                    props.onLogout?.();
                }}
                className="flex items-center gap-3 px-3 py-2 mt-2 font-medium text-red-600 rounded-lg group text-theme-sm hover:bg-red-50"
            >
                <LogOut size={18} />
                Logout
            </button>
        </Dropdown>
    );
};

export default AdminDetailsModal;
